import { createContext, useCallback, useContext, useEffect, useState } from "react";

// NOTE: this is client-side only. It keeps the password out of the bundle
// (only a SHA-256 hash ships) and adds expiry + lockout, but anyone with
// devtools access to the device can still bypass it. Real protection needs
// a backend.

const SESSION_KEY = "mm_session";
const ATTEMPTS_KEY = "mm_login_attempts";
const CREDENTIALS_KEY = "mm_credentials";
const REMEMBER_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const SESSION_MS = 12 * 60 * 60 * 1000; // 12 hours
const MAX_ATTEMPTS = 5;
const LOCK_MS = 60 * 1000;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const sha256 = async (text) => {
  if (!window.crypto?.subtle) {
    throw new Error("Secure login needs HTTPS (or localhost).");
  }
  const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const readJson = (storage, key) => {
  try {
    return JSON.parse(storage.getItem(key)) || null;
  } catch {
    return null;
  }
};

const readSession = () => {
  const s = readJson(localStorage, SESSION_KEY) || readJson(sessionStorage, SESSION_KEY);
  return s && s.expires > Date.now() ? s : null;
};

// Credentials changed in Settings win over the build-time ones.
const getCredentials = async () => {
  const saved = readJson(localStorage, CREDENTIALS_KEY);
  if (saved?.email && saved?.hash) return saved;
  const env = process.env;
  if (env.REACT_APP_AUTH_EMAIL && env.REACT_APP_AUTH_PASSWORD_HASH) {
    return { email: env.REACT_APP_AUTH_EMAIL, hash: env.REACT_APP_AUTH_PASSWORD_HASH.toLowerCase() };
  }
  // Legacy plaintext variables.
  if (env.REACT_APP_EMAIL && env.REACT_APP_Password) {
    return { email: env.REACT_APP_EMAIL, hash: await sha256(env.REACT_APP_Password) };
  }
  return null;
};

export const getLockRemaining = () => {
  const a = readJson(localStorage, ATTEMPTS_KEY);
  return a?.lockedUntil ? Math.max(0, a.lockedUntil - Date.now()) : 0;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(readSession);

  useEffect(() => {
    // Old builds trusted a plain `auth=true` cookie; remove it.
    document.cookie = "auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
  }, []);

  // Expire the session while the app is open.
  useEffect(() => {
    if (!session) return;
    const t = setTimeout(logout, Math.min(session.expires - Date.now(), 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [session, logout]);

  const login = async (email, password, remember) => {
    const lock = getLockRemaining();
    if (lock > 0) {
      return { ok: false, error: `Too many attempts. Try again in ${Math.ceil(lock / 1000)}s.` };
    }
    const creds = await getCredentials();
    if (!creds) {
      return { ok: false, error: "Login is not configured. Set REACT_APP_AUTH_EMAIL and REACT_APP_AUTH_PASSWORD_HASH." };
    }
    const hash = await sha256(password);
    if (email.trim().toLowerCase() !== creds.email.toLowerCase() || hash !== creds.hash) {
      const prev = readJson(localStorage, ATTEMPTS_KEY) || { count: 0 };
      const count = prev.count + 1;
      const next = count >= MAX_ATTEMPTS ? { count: 0, lockedUntil: Date.now() + LOCK_MS } : { count };
      localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(next));
      const left = MAX_ATTEMPTS - count;
      return {
        ok: false,
        error:
          count >= MAX_ATTEMPTS
            ? "Too many attempts. Login locked for 1 minute."
            : `Wrong email or password. ${left} attempt${left === 1 ? "" : "s"} left.`,
      };
    }
    localStorage.removeItem(ATTEMPTS_KEY);
    const s = { email: creds.email, expires: Date.now() + (remember ? REMEMBER_MS : SESSION_MS) };
    (remember ? localStorage : sessionStorage).setItem(SESSION_KEY, JSON.stringify(s));
    setSession(s);
    return { ok: true };
  };

  const changeCredentials = async (currentPassword, newEmail, newPassword) => {
    const creds = await getCredentials();
    if (!creds || (await sha256(currentPassword)) !== creds.hash) {
      return { ok: false, error: "Current password is wrong." };
    }
    const email = (newEmail || creds.email).trim();
    const hash = newPassword ? await sha256(newPassword) : creds.hash;
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify({ email, hash }));
    return { ok: true };
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: !!session, user: session?.email, login, logout, changeCredentials }}
    >
      {children}
    </AuthContext.Provider>
  );
};
